import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { emptyContentType } from '../../shared/constants/end-point-constants';
import { environment } from '../../../environments/environment';
import { API } from '../../shared/models/api.model';
import { HttpErrorHandlerService } from '../http-error-handler/http-error-handler.service';
import { LoggerService } from '../logger/logger.service';
import { SERVER } from './../../shared/enum/server.enum';

@Injectable({
  providedIn: 'root'
})
export class HttpHandlerService {

  constructor(
    private http: HttpClient,
    private httpErrorHandlerService: HttpErrorHandlerService,
    private loggerService: LoggerService
  ) {
  }
  /**
   *
   *
   * Method for GET Request to the backend server
   *
   *
   *
   * @param api Declaring API Properties
   * @param [urlSearchParams]  Defining the URL Search Parameters, if any
   * @param [bodyParams=null] Providing the body parameters
   * @returns Returns an observable
   * @memberof HttpHandlerService
   */
  callServerGET(api: API, urlSearchParams?: HttpParams, bodyParams: any = null, resType = 'json'): Observable<any> {
    return this.callServer('get', api, urlSearchParams, bodyParams, resType);
  }

  /**
   *
   *
   * Method for PUT Request to the backend server
   *
   *
   *
   * @param api Declaring API Properties
   * @param [urlSearchParams]  Defining the URL Search Parameters, if any
   * @param [bodyParams=null] Providing the body parameters
   * @returns Returns an observable
   * @memberof HttpHandlerService
   */
  callServerPUT(api: API, urlSearchParams?: HttpParams, bodyParams: any = null): Observable<any> {
    return this.callServer('put', api, urlSearchParams, bodyParams);
  }

  /**
   *
   *
   * Method for POST Request to the backend server
   *
   *
   *
   * @param api Declaring API Properties
   * @param [urlSearchParams]  Defining the URL Search Parameters, if any
   * @param [bodyParams=null] Providing the body parameters
   * @returns Returns an observable
   * @memberof HttpHandlerService
   */
  callServerPOST(api: API, urlSearchParams?: HttpParams, bodyParams: any = null, contentType?: string): Observable<any> {
    return this.callServer('post', api, urlSearchParams, bodyParams, null, contentType);
  }

  /**
   *
   *
   * Method for DELETE Request to the backend server
   *
   *
   *
   * @param api Declaring API Properties
   * @param [urlSearchParams]  Defining the URL Search Parameters, if any
   * @param [bodyParams=null] Providing the body parameters
   * @returns Returns an observable
   * @memberof HttpHandlerService
   */
  callServerDELETE(api: API, urlSearchParams?: HttpParams, bodyParams: any = null): Observable<any> {
    return this.callServer('delete', api, urlSearchParams, bodyParams);
  }

  /**
   *
   *
   * Final method to invoke the http-client instance for communicating with backend service(s)
   *
   *
   *
   * @param method Method name to trigger API request - GET, POST, PUT or DELETE
   * @param api  Declaring API Properties
   * @param [urlSearchParams] Defining the URL Search Parameters, if any
   * @param [bodyParams=null] Providing the body parameters
   * @returns Returns an observable
   * @memberof HttpHandlerService
   */
  callServer(method: string, api: API, urlSearchParams?: HttpParams, bodyParams: any = null,
    resType = 'json', contentType?: string): Observable<any> {
    urlSearchParams = (urlSearchParams) ? (urlSearchParams) : new HttpParams();
    urlSearchParams = urlSearchParams.append('pointTo', (api.pointTo).toString());
    urlSearchParams = (api.isAuthRequired) ?
      urlSearchParams.append('isAuthRequired', (api.isAuthRequired).toString()) : urlSearchParams;
    urlSearchParams = (api.isCaptchaTokenRequired) ?
      urlSearchParams.append('isCaptchaTokenRequired', (api.isCaptchaTokenRequired).toString()) : urlSearchParams;
    urlSearchParams = (api.pointTo === SERVER.Local) ?
      urlSearchParams.append('cb', environment.cb.toString()) : urlSearchParams;

    const req = api.pointTo === SERVER.IDP ? this.http.request(method, this.buildPath(api), {
      body: bodyParams,
      params: urlSearchParams,
      headers: this.generateApiHeader(api, contentType),
      responseType: 'json',
      withCredentials: true
    }) : this.http.request(method, this.buildPath(api), {
      body: bodyParams,
      params: urlSearchParams,
      headers: this.generateApiHeader(api, contentType),
      responseType: resType === 'blob' ? 'blob' : 'json',
    });

    if (api.loggingRequired) {
      let logString = '';
      logString += 'apiPath: ';
      logString += this.buildPath(api);
      logString += ' method: ' + method.toUpperCase();
      logString += ' urlParam: ';
      logString += urlSearchParams ? urlSearchParams.toString() : '';
      logString += ' bodyParam: ';
      logString += bodyParams ? JSON.stringify(bodyParams) : '';
      this.loggerService.logInfo(logString);
    }
    return req.pipe(map((response) => {
      if (response === '') {
        return {};
      } else {
        if (response && response instanceof Array && response.some(r => r instanceof Object && 'errorCode' in r)) {
          // if there are error with the http status code 200 then we need handle them
          this.httpErrorHandlerService.handleExceptionWith200(response);
          return { foaError: true };
        }
      }
      return response;
    }), catchError(err => {
      if (api.path.indexOf('images') > -1 && api.pointTo === SERVER.Mashery) {
        return throwError(err);
      } else {
        return this.httpErrorHandlerService.handleHttpError(err);
      }
    }));
  }


  /**
   *
   *
   * Function to create the final api path to reach the backend server/services
   *
   *
   *
   * @param api Consists of various api configs defined in api-config.service
   * @returns Return the string api
   * @memberof HttpHandlerService
   */
  buildPath(api: API): string {
    let apiPath = '';
    const hybrisOccBasePath = (environment.production) ? window.location.origin : environment.hybrisBasePath;
    const hybrisApiContextRoot = '/api';
    const hybrisApiBaseUrl = '/v2';
    const magicBasePath = environment.magicBasePath;
    switch (api.pointTo) {
      case SERVER.Hybris: {
        apiPath = hybrisOccBasePath + hybrisApiContextRoot + hybrisApiBaseUrl + '/' + environment.hybrisBaseSite + '/' + api.path;
        break;
      }
      case SERVER.IDP: {
        apiPath = environment.hybrisIdpPath + '/' + api.path;
        break;
      }
      case SERVER.Local: {
        apiPath = 'assets/' + api.path;
        break;
      }
      case SERVER.Mashery: {
        apiPath = magicBasePath + api.path;
        break;
      }
      case SERVER.Public: {
        apiPath = hybrisOccBasePath + '/' + api.path;
        break;
      }
      case SERVER.Open: {
        apiPath = environment.hybrisSfBasepath + '/' + api.path;
        break;
      }
      default:
        break;
    }
    return apiPath;
  }

  /**
   *
   *
   * @name getApiForWindow
   * @description Function to fetch complete URL detail for redirection purpose
   * @param api Details for Api redirection
   * @param [urlSearchParams] Params to be appended in URL
   * @return
   * @memberof HttpHandlerService
   */
  getApiForWindow(api: API, urlSearchParams?: HttpParams): string {
    return (this.buildPath(api) + '?' + urlSearchParams.toString());
  }

  /**
   * Function to generate different api headers as per the SERVER it connects with
   *
   * @private
   * @param api Request param to serve the particular API
   * @returns Return the http header generated for the particular API
   * @memberof HttpHandlerService
   */
  private generateApiHeader(api: API, contentType = 'application/json'): HttpHeaders {
    let apiHeaders = new HttpHeaders();
    switch (api.pointTo) {
      case SERVER.IDP:
      case SERVER.Mashery:
      case SERVER.Hybris: {
        apiHeaders = contentType === emptyContentType ? apiHeaders : apiHeaders.append('Content-Type', contentType);
        break;
      }
    }
    return apiHeaders;
  }
}
